"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { useRouter } from "next/navigation";
import { encodeString } from "@/lib/utils";
import Link from "next/link";
import { setCookie, getCookie } from "cookies-next";
import {
  IconButton,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { TextField, Button, Alert } from "@mui/material";
import authenticationApi from "@/lib/authenticationApi";
import AuthLayout from "@/components/AuthLayout";
import menuItems from "@/lib/menuItems";
import {
  enhancedPrefetchPages,
  extractHrefsFromMenuItems,
  isDevelopmentMode,
} from "@/utils/prefetchUtils";
import WarningModal from "@/components/WarningModal";
import LoginCompanySelectionModal from "@/components/LoginCompanySelectionModal";
import { applyAuthSessionClient } from "@/lib/applyAuthSession";
import { clearAuthCookies, getAuthCookieOptions } from "@/lib/authCookies";
import {
  validateEmail,
  resolveSsoLaunchUrl,
  reportSsoError,
  readSsoAttemptContext,
  writeSsoAttemptContext,
  type ValidateEmailChoice,
} from "@/lib/ssoApi";
import {
  applyCompanySelection,
  isTruthyFlag,
  type CompanyAssociation,
} from "@/lib/companyAssociation";

type LoginStage = "email" | "password";

/** Temporary: skip IdP redirect and use password login only. */
const SSO_ENABLED = true;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [refererUrl, setRefererUrl] = useState("");
  const [stage, setStage] = useState<LoginStage>(
    SSO_ENABLED ? "email" : "password",
  );
  const [validatedEmail, setValidatedEmail] = useState("");
  const [ssoChoices, setSsoChoices] = useState<ValidateEmailChoice[] | null>(
    null,
  );
  const [isSsoChooserOpen, setIsSsoChooserOpen] = useState(false);
  const [isAccountLockedModalOpen, setIsAccountLockedModalOpen] =
    useState(false);
  const [accountLockedMessage, setAccountLockedMessage] = useState(
    "Your account is temporarily locked because of repeated incorrect login attempts. Select FORGOT PASSWORD to reset your password.",
  );
  const [isCompanySelectionModalOpen, setIsCompanySelectionModalOpen] =
    useState(false);
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState("/dashboard");
  const router = useRouter();

  const validateAbortRef = useRef<AbortController | null>(null);

  const redirectAfterLogin = (userPayType: string, redirectUrl: string) => {
    if (userPayType === "Free User") {
      window.location.href = "/free-user";
      return;
    }

    window.location.href = redirectUrl || "/dashboard";
  };

  const handleAccountLockedConfirm = () => {
    setIsAccountLockedModalOpen(false);
    router.push("/forgot-password");
  };

  const handleCompanySelection = (company: CompanyAssociation) => {
    applyCompanySelection(company);
    setIsCompanySelectionModalOpen(false);

    const userPayType = getCookie("user_pay_type") as string;
    redirectAfterLogin(userPayType, pendingRedirectUrl);
  };

  const handleCompanySelectionCancel = () => {
    setIsCompanySelectionModalOpen(false);
    clearAuthCookies();
    setIsSubmitting(false);
  };

  // Memoize the menu items to prevent unnecessary re-renders
  const memoizedMenuItems = useMemo(() => menuItems, []);

  useEffect(() => {
    // Clear any existing logout events when login page is opened
    // This prevents old logout events from interfering with new logins
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith("ca-logout-")) {
        localStorage.removeItem(key);
      }
    });
    setRefererUrl((localStorage.getItem("referer_url") as string) || "");
    localStorage.removeItem("referer_url");
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);

    const genericError = params.get("error")?.trim() || "";
    if (genericError) {
      setError(genericError);
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      window.history.replaceState(
        {},
        "",
        url.pathname + (url.search ? url.search : ""),
      );
      return;
    }

    const code = params.get("sso_error");
    if (!code) return;
    const apiMessage = params.get("sso_error_message")?.trim() || "";
    const slugFromUrl = params.get("sso_slug")?.trim() || "";
    const attemptContext = readSsoAttemptContext();
    const messages: Record<string, string> = {
      missing_slug: "Single sign-on is misconfigured (missing tenant).",
      acs_secret_missing:
        "SSO ACS is missing INTERNAL_API_SECRET on this app host. Set it in the Next.js environment and restart.",
      internal_auth_not_configured:
        "SSO API is missing INTERNAL_API_SECRET on the Rails host. Set the same secret on Next.js and Rails, then retry.",
      unauthorized_internal_caller:
        "SSO internal auth failed: Next.js INTERNAL_API_SECRET does not match Rails. Align both secrets and restart.",
      bad_post: "We could not read the sign-in response. Please try again.",
      missing_saml_response:
        "No sign-in response was received from your organization.",
      validate_unreachable:
        "We could not reach the sign-in service. Please try again shortly.",
      validate_unauthorized:
        "Sign-in was rejected. Your session may have expired—try again.",
      validate_failed: "Sign-in failed. Please try again or use another method.",
      validate_bad_json: "Sign-in completed with an unexpected response.",
      validate_no_token: "Sign-in did not return a valid session.",
      setting_not_found:
        "No SSO configuration was found for this organization (check the slug).",
      setting_disabled: "SSO is disabled for this organization.",
      signature_invalid:
        "The sign-in response signature was invalid. Check the IdP certificate.",
      audience_mismatch:
        "SSO Audience / SP Entity ID does not match this application.",
      issuer_mismatch:
        "SSO Issuer / IdP Entity ID does not match this application.",
      idp_initiated_disallowed:
        "IdP-initiated SSO is not enabled for this organization.",
      in_response_to_mismatch:
        "Sign-in response did not match the original request. Try again.",
      response_stale: "The sign-in response expired. Please try again.",
      response_expired: "The sign-in response expired. Please try again.",
      assertion_replayed: "This sign-in response was already used. Try again.",
      missing_email_attribute:
        "Your IdP did not send an email attribute required for sign-in.",
      user_not_provisioned:
        "No company user exists for this email in this organization. Create the company user first, then retry SSO.",
      jit_defaults_missing:
        "SSO JIT defaults are no longer required. Redeploy the latest sso_user_resolve_isp stored procedure, then retry.",
      user_inactive: "Your user account is inactive.",
      company_user_inactive: "Your company user account is inactive.",
      company_inactive: "This company account is inactive.",
      user_resolve_failed:
        "Sign-in succeeded at the IdP but user provisioning failed.",
    };
    // Prefer the SP/API message when present (status=error + message).
    const resolvedMessage =
      apiMessage ||
      messages[code] ||
      `Sign-in could not be completed (${code}). Please try again or contact support.`;
    setError(resolvedMessage);

    const contextEmail = attemptContext.email?.trim().toLowerCase() || "";
    if (contextEmail) {
      setEmail(contextEmail);
      setValidatedEmail(contextEmail);
    }

    void reportSsoError({
      errorCode: code,
      errorMessage: resolvedMessage,
      userEmail: contextEmail || undefined,
      slug: slugFromUrl || attemptContext.slug || undefined,
      source: "login_sso_error_redirect",
    });

    const url = new URL(window.location.href);
    url.searchParams.delete("sso_error");
    url.searchParams.delete("sso_error_message");
    url.searchParams.delete("sso_slug");
    window.history.replaceState(
      {},
      "",
      url.pathname + (url.search ? url.search : ""),
    );
  }, []);

  // Prefetch all menu and submenu pages
  useEffect(() => {
    if (!memoizedMenuItems || memoizedMenuItems.length === 0) return;

    const hrefs = extractHrefsFromMenuItems(memoizedMenuItems);

    if (hrefs.length === 0) {
      console.log("🚀 No valid routes to prefetch");
      return;
    }

    document.title = "CareAscendium - Login";

    const performPrefetch = async () => {
      await enhancedPrefetchPages({
        router,
        hrefs,
        delay: 100,
        enableLogging: true,
      });

      if (isDevelopmentMode()) {
        console.log(
          "🔧 Development mode: Using enhanced prefetching with fallbacks",
        );
        console.log(
          "💡 To test full prefetching, run: npm run build && npm run start",
        );
      }
    };

    performPrefetch();
  }, [memoizedMenuItems, router]);

  // If the user edits the email after we've moved past Stage 1, snap back
  // to Stage 1 so the password field is hidden and the validate_email
  // round-trip runs again. This is the "self-revert" requirement.
  const handleEmailChange = (next: string) => {
    setEmail(next);
    if (error) setError("");
    if (
      SSO_ENABLED &&
      stage === "password" &&
      next.toLowerCase() !== validatedEmail
    ) {
      setStage("email");
      setPassword("");
      setShowPassword(false);
    }
  };

  // Stage 1 handler: validate the email's domain and decide what to do.
  const handleValidateEmail = async () => {
    if (!SSO_ENABLED) {
      setValidatedEmail(email.trim().toLowerCase());
      setStage("password");
      return;
    }

    setError("");
    const v_email = email.trim().toLowerCase();
    if (!v_email || !v_email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    if (validateAbortRef.current) {
      validateAbortRef.current.abort();
    }
    const controller = new AbortController();
    validateAbortRef.current = controller;

    setIsValidating(true);
    try {
      const result = await validateEmail(v_email, { signal: controller.signal });

      if (!result || result.sso === false) {
        setValidatedEmail(v_email);
        setStage("password");
        setSsoChoices(null);
        return;
      }

      // SSO is enabled. If the API returned multiple matching tenants,
      // ask the user to pick one; otherwise launch immediately.
      if ("choices" in result) {
        const choices = result.choices;
        if (choices.length === 1) {
          launchSso(choices[0]);
          return;
        }
        setSsoChoices(choices);
        setIsSsoChooserOpen(true);
        return;
      }

      // Single-tenant SSO response (choices branch narrowed away above).
      launchSso(result);
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === "AbortError") return;
      console.error("validate_email failed", err);
      const fallbackMessage =
        "We couldn't verify that email right now. Please try again in a moment.";
      setError(fallbackMessage);
      void reportSsoError({
        errorCode: "validate_email_failed",
        errorMessage:
          err instanceof Error ? err.message : fallbackMessage,
        userEmail: v_email,
        source: "login_validate_email",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const launchSso = (choice: ValidateEmailChoice) => {
    writeSsoAttemptContext({
      email: email.trim().toLowerCase() || validatedEmail || undefined,
      slug: choice.slug || undefined,
    });
    const url = resolveSsoLaunchUrl(choice);
    setIsValidating(true);
    window.location.href = url;
  };

  // Stage 2 handler: classic password login.
  const handlePasswordSubmit = async () => {
    setError("");
    setIsSubmitting(true);

    const v_email = email.trim().toLowerCase();
    const encrypted_password = encodeString(v_email + password);

    const postParams = {
      email: v_email,
      password: encrypted_password,
    };

    await authenticationApi
      .post(`/v1/user_authentication/authenticate`, postParams)
      .then(async (response: { data: Record<string, unknown> }) => {
        // Defense in depth: even if the user reached Stage 2, the server
        // can still tell us "this account is force-SSO" and block here.
        const data = response.data as Record<string, unknown>;
        if (SSO_ENABLED && data?.sso_required === true) {
          handleSsoRequiredFromAuthenticate(data);
          return;
        }

        applyAuthSessionClient(data as Parameters<typeof applyAuthSessionClient>[0]);

        // Clear any existing logout events after successful login
        // This ensures clean state for the new user session
        const keys = Object.keys(localStorage);
        keys.forEach((key) => {
          if (key.startsWith("ca-logout-")) {
            localStorage.removeItem(key);
          }
        });

        // Broadcast login event to close other tabs with the same user
        const caUserId = response.data["ca_user_id"];
        if (caUserId) {
          // Set a flag to indicate this tab is the one that just logged in
          sessionStorage.setItem("just-logged-in", "true");
          localStorage.setItem(`ca-login-${caUserId}`, Date.now().toString());
          console.log(
            "Login: Broadcasting login event to close other tabs for user:",
            caUserId,
          );
        }

        const redirectUrl = "/dashboard";
        const requiresCompanySelection = isTruthyFlag(
          response.data["user_company_association_flag"],
        );

        setIsSubmitting(false);

        if (requiresCompanySelection) {
          setPendingRedirectUrl(redirectUrl);
          setIsCompanySelectionModalOpen(true);
          return;
        }

        redirectAfterLogin(response.data["user_pay_type"] as string, redirectUrl);
      })
      .catch((err: { details?: Record<string, unknown>; message?: string }) => {
        console.log(err);
        setIsSubmitting(false);

        const details = err.details || {};
        if (SSO_ENABLED && details.sso_required === true) {
          handleSsoRequiredFromAuthenticate(details);
          return;
        }

        const resolvedMessage =
          (details.error_message as string) || err.message || "Login failed.";

        if (details.account_locked === true) {
          setCookie("locked_email", v_email, getAuthCookieOptions());
          setAccountLockedMessage(
            resolvedMessage ||
              "Your account is temporarily locked because of repeated incorrect login attempts. Select FORGOT PASSWORD to reset your password.",
          );
          setIsAccountLockedModalOpen(true);
        } else {
          setError(resolvedMessage);
        }
      });
  };

  /**
   * The Rails authenticate endpoint refused password login because the
   * tenant has force_sso=true. Either redirect to the IdP straight away
   * or, if multiple tenants match (rare for a single email), let the
   * user pick.
   */
  const handleSsoRequiredFromAuthenticate = (
    payload: Record<string, unknown>,
  ) => {
    const choice: ValidateEmailChoice = {
      mode: (payload.mode as ValidateEmailChoice["mode"]) || "sp_initiated",
      slug: (payload.slug as string) || "",
      init_url: payload.init_url as string | undefined,
      redirect_url: payload.redirect_url as string | undefined,
      provider_label: payload.provider_label as string | undefined,
      company_name: payload.company_name as string | undefined,
    };
    if (!choice.slug) {
      setError(
        (payload.error_message as string) ||
          "Single Sign-On is required for this account.",
      );
      return;
    }
    launchSso(choice);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (SSO_ENABLED && stage === "email") {
      handleValidateEmail();
    } else {
      handlePasswordSubmit();
    }
  };

  const primaryButtonLabel = (() => {
    if (stage === "email") {
      return isValidating ? "CHECKING…" : "NEXT";
    }
    return isSubmitting ? "LOGGING IN…" : "LOG IN";
  })();

  const isPrimaryDisabled =
    isSubmitting ||
    isValidating ||
    (SSO_ENABLED && stage === "email" ? !email : !email || !password);

  return (
    <AuthLayout
      title="LogIn"
      welcomeTitle="Welcome Back :)"
      leftBackgroundImage="/login-bg.jpg"
      darkTheme
      logoImage="/auth-white-logo.png"
      tagline="QUALITY. PRODUCTIVITY. COMPOUNDED."
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <form
        onSubmit={handleSubmit}
        className="space-y-4 sm:space-y-6"
        autoComplete="on"
      >
        <div>
          <label className="block text-sm font-semibold text-white/90 mb-1.5">
            Email <span className="text-red-500">*</span>
          </label>
          <TextField
            type="email"
            name="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            fullWidth
            required
            variant="outlined"
            placeholder="johnsmith@gmail.com"
            error={!!error && !email}
            helperText={error && !email ? "Email is required" : ""}
            InputLabelProps={{ shrink: true }}
            autoFocus={!SSO_ENABLED || stage === "email"}
            disabled={isValidating || isSubmitting}
            slotProps={{ htmlInput: { autoComplete: "username" } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                fontSize: "1rem",
                backgroundColor: "#fff",
                color: "#1e293b",
                "& fieldset": { borderColor: "#cbd5e1" },
                "&:hover fieldset": { borderColor: "#94a3b8" },
                "&.Mui-focused fieldset": {
                  borderWidth: "2px",
                  borderColor: "#153C7F",
                },
              },
              "& .MuiFormHelperText-root": {
                color: "#fca5a5",
                marginTop: "4px",
              },
            }}
          />
        </div>

        <div
          aria-hidden={SSO_ENABLED && stage === "email"}
          className={
            SSO_ENABLED && stage === "email"
              ? "absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0 [clip:rect(0,0,0,0)]"
              : undefined
          }
        >
          {(stage === "password" || SSO_ENABLED) && (
            <>
              <label
                className={
                  SSO_ENABLED && stage === "email"
                    ? "sr-only"
                    : "block text-sm font-semibold text-white/90 mb-1.5"
                }
              >
                Password <span className="text-red-500">*</span>
              </label>
              <TextField
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required={stage === "password"}
                variant="outlined"
                error={!!error && stage === "password" && !password}
                helperText={
                  error && stage === "password" && !password
                    ? "Password is required"
                    : ""
                }
                InputLabelProps={{ shrink: true }}
                autoFocus={SSO_ENABLED && stage === "password"}
                tabIndex={stage === "password" ? 0 : -1}
                slotProps={{ htmlInput: { autoComplete: "current-password" } }}
                InputProps={{
                  endAdornment:
                    stage === "password" ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: "#64748b" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    fontSize: "1rem",
                    backgroundColor: "#fff",
                    color: "#1e293b",
                    "& fieldset": { borderColor: "#cbd5e1" },
                    "&:hover fieldset": { borderColor: "#94a3b8" },
                    "&.Mui-focused fieldset": {
                      borderWidth: "2px",
                      borderColor: "#153C7F",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    color: "#fca5a5",
                    marginTop: "4px",
                  },
                }}
              />
            </>
          )}
        </div>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isPrimaryDisabled}
          startIcon={
            isValidating || isSubmitting ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : undefined
          }
          sx={{
            py: { xs: 2, sm: 1.75 },
            mt: 0.5,
            borderRadius: "6px",
            backgroundColor: "#63A821",
            color: "#fff",
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "0 2px 12px rgba(99,168,33,0.35)",
            "&:hover": {
              backgroundColor: "#4e8c1a",
              boxShadow: "0 4px 14px rgba(99,168,33,0.4)",
            },
            "&.Mui-disabled": {
              backgroundColor: "#63A821",
              color: "#fff",
              opacity: 0.9,
            },
          }}
        >
          {primaryButtonLabel}
        </Button>

        <div className="mt-3 text-right">
          <Link
            href="/forgot-password"
            className="text-sm text-gray-400 italic hover:text-gray-300 hover:not-italic underline font-medium"
          >
            Forgot Password?
          </Link>
        </div>
      </form>

      <LoginCompanySelectionModal
        open={isCompanySelectionModalOpen}
        onSelect={handleCompanySelection}
        onCancel={handleCompanySelectionCancel}
      />

      <WarningModal
        open={isAccountLockedModalOpen}
        onClose={() => setIsAccountLockedModalOpen(false)}
        onPrimary={handleAccountLockedConfirm}
        type="warning"
        title="ACCOUNT LOCKED"
        message={accountLockedMessage}
        primaryButtonText="FORGOT PASSWORD"
        secondaryButtonText="CANCEL"
        onSecondary={() => setIsAccountLockedModalOpen(false)}
      />

      <Dialog
        open={isSsoChooserOpen}
        onClose={() => setIsSsoChooserOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 700,
            fontSize: "20px",
            color: "#153C7F",
          }}
        >
          CHOOSE AN ORGANIZATION
        </DialogTitle>
        <DialogContent dividers>
          <List>
            {(ssoChoices || []).map((choice) => (
              <ListItemButton
                key={choice.slug}
                onClick={() => {
                  setIsSsoChooserOpen(false);
                  launchSso(choice);
                }}
              >
                <ListItemText
                  primary={choice.company_name || choice.slug}
                  secondary={choice.provider_label || choice.mode}
                  primaryTypographyProps={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                  }}
                  secondaryTypographyProps={{
                    fontFamily: "Poppins, sans-serif",
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setIsSsoChooserOpen(false)}
            sx={{
              backgroundColor: "#F3F1FF",
              color: "#767C88",
              border: "1px solid #F3F1FF",
              borderRadius: "6px",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 400,
              textTransform: "none",
              "&:hover": { backgroundColor: "#E8E5FF" },
            }}
          >
            CANCEL
          </Button>
        </DialogActions>
      </Dialog>
    </AuthLayout>
  );
}
